import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Rating,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isMobile } from 'react-device-detect';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import axios from '../axiosConfig';
import { useTranslation } from 'react-i18next';

const RatePortal = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [hasExistingRating, setHasExistingRating] = useState(false);
  const [existingRatingDate, setExistingRatingDate] = useState(null);

  const buttonLabel = hasExistingRating ? (isRTL ? 'تحديث التقييم' : 'Update Rating') : t('submitRating');
  const isExtreme = rating === 1 || rating === 5;

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchMyRatings();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      const response = await axios.get('/Rating/Summary');
      console.log('📊 Summary Response:', response);
      // Extract dataItem from the response
      const data = response.data.dataItem;
      setSummary(data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchMyRatings = async () => {
    try {
      const response = await axios.get('/Rating/MyRatings');
      // Extract dataList from the response
      const list = response.data.dataList;
      if (Array.isArray(list) && list.length > 0) {
        const latest = list[0];
        setRating(latest?.rating ?? 0);
        setComment(latest?.comment ?? '');
        setHasExistingRating(true);
        setExistingRatingDate(latest?.ratingDate ?? null);
      } else {
        setHasExistingRating(false);
      }
    } catch (err) {
      console.error('Error fetching my ratings:', err);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError(t('pleaseSelectRating'));
      return;
    }
    if ((rating === 1 || rating === 5) && (!comment || comment.trim().length === 0)) {
      setError(isRTL ? 'يرجى إضافة تعليق للتقييم 1 أو 5' : 'Please add a comment for 1 or 5-star rating');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await axios.post('/Rating', {
        rating: rating,
        comment: comment
      });
      
      // Check if the response status is Success
      if (response.data.status === "Success") {
        setSuccess(true);
        // Refresh summary and my rating after successful submission
        await fetchSummary();
        await fetchMyRatings();

        // Hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        // Handle error status from server
        setError(response.data.message || t('errorSubmittingRating'));
      }
    } catch (err) {
      const serverMessage = err?.response?.data?.message || err?.message || t('errorSubmittingRating');
      setError(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, color }) => (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: isRTL ? 0 : 2,
              ml: isRTL ? 2 : 0
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold" color={color}>
          {loadingSummary ? <CircularProgress size={24} /> : value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4, mb: 4 }}>
      {/* Rating Section */}
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 4,
          mb: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('rateYourExperience')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('ratePortalDescription')}
          </Typography>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('thankYouForRating')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {hasExistingRating && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isRTL ? 'يتم تحديث تقييمك السابق' : 'You are updating your previous rating'}
          </Typography>
        )}
        <Box textAlign="center" mb={3}>
          <Rating
            name="portal-rating"
            value={rating}
            onChange={(event, newValue) => setRating(newValue)}
            size="large"
            dir="ltr"
            sx={{
              fontSize: isMobile ? '3rem' : '4rem',
              '& .MuiRating-iconFilled': {
                color: theme.palette.warning.main
              },
              '& .MuiRating-iconHover': {
                color: theme.palette.warning.main
              },
              '& .MuiRating-icon': {
                transition: 'transform 0.2s ease-in-out',
              },
              '& .MuiRating-iconEmpty': {
                color: theme.palette.action.disabled
              }
            }}
          />
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary">
              {rating === 0 && t('selectRating')}
              {rating === 1 && t('rating1')}
              {rating === 2 && t('rating2')}
              {rating === 3 && t('rating3')}
              {rating === 4 && t('rating4')}
              {rating === 5 && t('rating5')}
            </Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          label={isRTL ? `تعليقك ${isExtreme ? '(مطلوب للتقييم 1 أو 5)' : '(اختياري)'}` : `Your Comment ${isExtreme ? '(required for 1 or 5-star)' : '(optional)'}`}
          placeholder={t('shareYourThoughts')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          required={isExtreme}
          helperText={isExtreme ? (isRTL ? 'التعليق مطلوب للتقييم 1 أو 5' : 'Comment is required for 1 or 5-star rating') : ''}
          sx={{ mb: 3 }}
        />

        <Box textAlign="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading || rating === 0}
            sx={{
              minWidth: 200,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            {loading ? <CircularProgress size={24} /> : buttonLabel}
          </Button>
        </Box>
      </Paper>

      {/* Statistics Section */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
          {t('portalStatistics')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleIcon sx={{ color: 'primary.main', fontSize: 40 }} />}
            title={t('registeredCitizens')}
            value={summary?.totalUsers || 0}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircleIcon sx={{ color: 'success.main', fontSize: 40 }} />}
            title={t('servicesUsed')}
            value={summary?.totalServicesUsed || 0}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<StarIcon sx={{ color: 'warning.main', fontSize: 40 }} />}
            title={t('averageRating')}
            value={summary?.averageRating || 0}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<RateReviewIcon sx={{ color: 'info.main', fontSize: 40 }} />}
            title={t('totalRatings')}
            value={summary?.totalRatings || 0}
            color="info.main"
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default RatePortal;
